/* eslint-disable import/export */
import { pipe } from 'fp-ts/function';
import * as Ord from 'fp-ts/Ord';
import * as RA from 'fp-ts/ReadonlyArray';
import * as s from 'fp-ts/string';
export * from 'fp-ts/struct';
/**
 * Creates a new object by recursively evolving a shallow copy of `a`, according
 * to the `transformation` functions.
 *
 * **CUSTOM IMPLEMENTATION TO SUPPORT ALSO SYMBOLS**
 *
 * @example
 * import { pipe } from 'fp-ts/function'
 * import { evolve } from 'fp-ts/struct'
 *
 * assert.deepStrictEqual(
 *   pipe(
 *     { a: 'a', b: 1 },
 *     evolve({
 *       a: (a) => a.length,
 *       b: (b) => b * 2
 *     })
 *   ),
 *   { a: 1, b: 2 }
 * )
 *
 * @since 2.11.0
 */
export var evolve = function (transformations) {
    return function (a) {
        var out = {};
        Object.getOwnPropertyNames(transformations).forEach(function (k) {
            out[k] = transformations[k](a[k]);
        });
        Object.getOwnPropertySymbols(transformations).forEach(function (k) {
            out[k] = transformations[k](a[k]);
        });
        return out;
    };
};
/**
 * Applies one value to all fields of a struct.
 *
 * ### Example
 * ``` ts
 * pipe(
 *   'Foo',
 *   fork({
 *     uppercase: s => s.toUpperCase(),
 *     lowercase: s => s.toLowerCase(),
 *     length: s => s.length
 *   })
 * )
 * ```
 */
export var fork = function (transformations) {
    return function (a) {
        var out = {};
        Object.getOwnPropertyNames(transformations).forEach(function (k) {
            out[k] = transformations[k](a);
        });
        Object.getOwnPropertySymbols(transformations).forEach(function (k) {
            out[k] = transformations[k](a);
        });
        return out;
    };
};
/**
 * Reduces a struct following a given Monoid. The keys are sorted by the given
 * Ord.
 *
 * ### Example
 * ```ts
 * pipe(
 *   {
 *     foo: 'foo',
 *     bar: [1, 2, 3]
 *   },
 *   foldMap(OrdKey)(n.MonoidSum)({
 *     foo: s => s.length,
 *     bar: a => a.length
 *   })
 * )
 * ```
 */
export var foldMap = function (ord) {
    return function (m) {
        return function (transformations) {
            return function (a) {
                return pipe([
                    Object.getOwnPropertyNames(transformations),
                    Object.getOwnPropertySymbols(transformations)
                ], RA.flatten, RA.sort(ord), RA.foldMap(m)(function (k) { return transformations[k](a[k]); }));
            };
        };
    };
};
/**
 * An Ord for object keys (i.e. `number | string | symbol`).
 */
export var OrdKey = Ord.fromCompare(function (first, second) {
    var _a, _b;
    var a;
    var b;
    if (typeof first === 'symbol') {
        a = (_a = first.description) !== null && _a !== void 0 ? _a : '';
    }
    else if (typeof first === 'number') {
        a = first.toString();
    }
    else {
        a = first;
    }
    if (typeof second === 'symbol') {
        b = (_b = second.description) !== null && _b !== void 0 ? _b : '';
    }
    else if (typeof second === 'number') {
        b = second.toString();
    }
    else {
        b = second;
    }
    return s.Ord.compare(a, b);
});
